import React, { useState, useEffect, useRef } from 'react';
import API from '../../utils/api';
import { getUploadUrl, formatDateTime } from '../../utils/config';
import toast from 'react-hot-toast';
import { FaSearch, FaDownload, FaEye, FaTrash, FaFilter, FaUser, FaExclamationTriangle, FaPen, FaCheck, FaClock, FaTimes } from 'react-icons/fa';
import MemberEditModal from '../../components/MemberEditModal';
import { useAuth } from '../../context/AuthContext';

export default function AllRegistrations() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchMembers(); }, [search, statusFilter]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await API.get(`/members?${params}`);
      setMembers(res.data.members || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (memberToUpdate, newStatus) => {
    try {
      await API.put(`/members/${memberToUpdate.id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      setMembers(prev => prev.map(m => m.id === memberToUpdate.id ? { ...m, status: newStatus } : m));
      if (selectedMember && selectedMember.id === memberToUpdate.id) {
        setSelectedMember({ ...selectedMember, status: newStatus });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;
    setIsDeleting(true);
    try {
      await API.delete(`/members/${memberToDelete.id}`);
      toast.success(`Member ${memberToDelete.name || ''} deleted`);
      if (selectedMember?.id === memberToDelete.id) {
        setSelectedMember(null);
      }
      setMemberToDelete(null);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete member');
    } finally {
      setIsDeleting(false);
    }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await API.get('/members/export/csv', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `members_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Members CSV exported successfully');
    } catch (err) {
      console.error('Export CSV error:', err);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };


  return (
    <div>
      {/* Topbar */}
      <div className="topbar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>All Registrations</h1>
          <p style={{ fontSize: 13, color: '#6b7280' }}>{total} total members registered</p>
        </div>
        <div>
          <button className="btn btn-outline btn-sm" onClick={exportCSV} disabled={exporting}>
            <FaDownload size={13} /> {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          {/* Filters */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="search-box" style={{ width: 300 }}>
              <FaSearch color="#9ca3af" size={14} />
              <input placeholder="Search by name, mobile, member ID..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select
              className="form-select"
              style={{ width: 160 }}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
              Showing {members.length} members
            </span>
          </div>

          {/* Table */}
          <div className="table-container">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
                <div className="spinner"></div>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Member ID</th>
                    <th>Name</th>
                    <th>Father Name</th>
                    <th>Mobile</th>
                    <th>Village</th>
                    <th>District</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 ? (
                    <tr><td colSpan={11}>
                      <div className="empty-state">
                        <div className="empty-state-icon">👥</div>
                        <h3>No members found</h3>
                        <p>Try adjusting your search filters</p>
                      </div>
                    </td></tr>
                  ) : members.map((m, i) => (
                    <tr key={m.id}>
                      <td style={{ color: '#9ca3af', fontSize: 12 }}>{i + 1}</td>
                      <td><span style={{ fontFamily: 'monospace', color: '#16a34a', fontWeight: 700, fontSize: 13 }}>{m.member_id}</span></td>
                      <td style={{ fontWeight: 600 }}>{m.name}</td>
                      <td>{m.father_name || '—'}</td>
                      <td>{m.mobile_phone || '—'}</td>
                      <td>{m.village || '—'}</td>
                      <td>{m.district || '—'}</td>
                      <td>{m.category || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span className={`badge ${m.status === 'approved' ? 'badge-success' : m.status === 'pending' ? 'badge-pending' : 'badge-danger'}`}>
                            {m.status}
                          </span>
                          {isSuperAdmin && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <button
                                type="button"
                                onClick={() => m.status !== 'approved' && handleStatusChange(m, 'approved')}
                                title={m.status === 'approved' ? 'Current: Approved' : 'Approve member'}
                                style={{
                                  border: m.status === 'approved' ? '1px solid #16a34a' : '1px solid #bbf7d0',
                                  background: m.status === 'approved' ? '#16a34a' : '#f0fdf4',
                                  color: m.status === 'approved' ? '#ffffff' : '#16a34a',
                                  width: 22,
                                  height: 22,
                                  borderRadius: 4,
                                  cursor: m.status === 'approved' ? 'default' : 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  transition: 'all 0.15s'
                                }}
                              >
                                <FaCheck size={10} />
                              </button>
                              <button
                                type="button"
                                onClick={() => m.status !== 'pending' && handleStatusChange(m, 'pending')}
                                title={m.status === 'pending' ? 'Current: Pending' : 'Mark as pending'}
                                style={{
                                  border: m.status === 'pending' ? '1px solid #d97706' : '1px solid #fde68a',
                                  background: m.status === 'pending' ? '#d97706' : '#fffbeb',
                                  color: m.status === 'pending' ? '#ffffff' : '#d97706',
                                  width: 22,
                                  height: 22,
                                  borderRadius: 4,
                                  cursor: m.status === 'pending' ? 'default' : 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  transition: 'all 0.15s'
                                }}
                              >
                                <FaClock size={10} />
                              </button>
                              <button
                                type="button"
                                onClick={() => m.status !== 'rejected' && handleStatusChange(m, 'rejected')}
                                title={m.status === 'rejected' ? 'Current: Rejected' : 'Reject member'}
                                style={{
                                  border: m.status === 'rejected' ? '1px solid #dc2626' : '1px solid #fecaca',
                                  background: m.status === 'rejected' ? '#dc2626' : '#fef2f2',
                                  color: m.status === 'rejected' ? '#ffffff' : '#dc2626',
                                  width: 22,
                                  height: 22,
                                  borderRadius: 4,
                                  cursor: m.status === 'rejected' ? 'default' : 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  transition: 'all 0.15s'
                                }}
                              >
                                <FaTimes size={10} />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const dt = formatDateTime(m.created_at);
                          return (
                            <div>
                              <div style={{ fontWeight: 600, color: '#111827' }}>{dt.date}</div>
                              {dt.time && (
                                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                                  {dt.time}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setSelectedMember(m)}
                            title="View Details"
                            style={{ padding: '4px 10px' }}
                          >
                            <FaEye size={12} />
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => setMemberToEdit(m)}
                            title="Edit Member"
                            style={{ padding: '4px 10px', color: '#16a34a', borderColor: '#bbf7d0' }}
                          >
                            <FaPen size={11} />
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => setMemberToDelete(m)}
                            title="Delete Member"
                            style={{ padding: '4px 10px' }}
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Member Edit Modal */}
      {memberToEdit && (
        <MemberEditModal
          member={memberToEdit}
          isAdmin={true}
          onClose={() => setMemberToEdit(null)}
          onSuccess={(updated) => {
            setMembers(prev => prev.map(m => (m.id === updated.id || m.member_id === updated.member_id) ? updated : m));
            if (selectedMember && (selectedMember.id === updated.id || selectedMember.member_id === updated.member_id)) {
              setSelectedMember(updated);
            }
          }}
        />
      )}

      {/* Member Detail Modal */}
      {selectedMember && (
        <MemberDetailModal
          member={selectedMember}
          isSuperAdmin={isSuperAdmin}
          onStatusChange={handleStatusChange}
          onClose={() => setSelectedMember(null)}
          onEditClick={(mem) => {
            setMemberToEdit(mem);
          }}
          onDeleteClick={(mem) => {
            setSelectedMember(null);
            setMemberToDelete(mem);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {memberToDelete && (
        <div className="modal-overlay" onClick={() => !isDeleting && setMemberToDelete(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, background: '#fee2e2', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaExclamationTriangle color="#ef4444" size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>Delete Registration</h3>
                  <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>This action cannot be undone</p>
                </div>
              </div>
              <button
                disabled={isDeleting}
                onClick={() => setMemberToDelete(null)}
                style={{ border: 'none', background: '#f3f4f6', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16 }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ paddingTop: 16 }}>
              <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.5, margin: 0 }}>
                Are you sure you want to permanently delete this member registration?
              </p>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginTop: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#991b1b' }}>{memberToDelete.name}</div>
                <div style={{ fontSize: 13, color: '#b91c1c', fontFamily: 'monospace', marginTop: 2 }}>ID: {memberToDelete.member_id}</div>
                {memberToDelete.mobile_phone && (
                  <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 2 }}>Phone: {memberToDelete.mobile_phone}</div>
                )}
              </div>
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 12, marginBottom: 0 }}>
                This will delete the registration details and any associated user login credentials.
              </p>
            </div>
            <div className="modal-footer" style={{ borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={isDeleting}
                onClick={() => setMemberToDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <FaTrash size={12} />
                {isDeleting ? 'Deleting...' : 'Delete Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemberDetailModal({ member: m, isSuperAdmin, onStatusChange, onClose, onEditClick, onDeleteClick }) {
  const fields = [
    { label: 'Member ID', value: m.member_id },
    { label: 'Name', value: m.name },
    { label: 'Father Name', value: m.father_name },
    { label: 'Mother Name', value: m.mother_name },
    { label: 'Spouse Name', value: m.spouse_name },
    { label: 'Full Address', value: m.full_address },
    { label: 'Village', value: m.village },
    { label: 'Post', value: m.post },
    { label: 'Hobli', value: m.hobli },
    { label: 'Taluk', value: m.taluk },
    { label: 'District', value: m.district },
    { label: 'PIN', value: m.pin },
    { label: 'Mobile Phone', value: m.mobile_phone },
    { label: 'Email', value: m.email },
    { label: 'Aadhaar No', value: m.aadhaar_no },
    { label: 'PAN', value: m.pan },
    { label: 'Voter ID', value: m.voter_id },
    { label: 'Ration Card No', value: m.ration_card_no },
    { label: 'Driving License', value: m.driving_license_no },
    { label: 'Passport No', value: m.passport_no },
    { label: 'Category', value: m.category },
    { label: 'Status', value: m.status },
    { label: 'Registration Date & Time', value: formatDateTime(m.created_at).full },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, background: '#f0fdf4', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaUser color="#16a34a" size={18} />
            </div>
            <div>
              <h2 style={{ fontWeight: 700, fontSize: 18 }}>{m.name}</h2>
              <span style={{ fontFamily: 'monospace', color: '#16a34a', fontWeight: 600, fontSize: 14 }}>{m.member_id}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f3f4f6', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div className="modal-body">
          {/* Photo & Signature */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            {m.photo_path && (
              <div style={{ textAlign: 'center' }}>
                <img src={getUploadUrl(m.photo_path)} alt="Member"
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 12, border: '2px solid #bbf7d0' }} />
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Photo</div>
              </div>
            )}
            {m.signature_path && (
              <div style={{ textAlign: 'center' }}>
                <img src={getUploadUrl(m.signature_path)} alt="Signature"
                  style={{ width: 120, height: 50, objectFit: 'contain', borderRadius: 8, border: '2px solid #bbf7d0', background: '#f9fafb' }} />
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Signature</div>
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {fields.map(f => f.value && (
              <div key={f.label} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{f.label}</div>
                <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>{f.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {onEditClick && (
              <button
                className="btn btn-outline btn-sm"
                onClick={() => onEditClick(m)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#16a34a', borderColor: '#bbf7d0' }}
              >
                <FaPen size={11} /> Edit Member
              </button>
            )}
            {isSuperAdmin && onStatusChange && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f3f4f6', padding: '3px 8px', borderRadius: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Approval:</span>
                <select
                  className="form-select"
                  style={{ width: 'auto', padding: '3px 8px', fontSize: 12, height: 28 }}
                  value={m.status}
                  onChange={(e) => onStatusChange(m, e.target.value)}
                >
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            )}
            {onDeleteClick && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => onDeleteClick(m)}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <FaTrash size={12} /> Delete Member
              </button>
            )}
          </div>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
